import React, { useEffect, useMemo, useState } from 'react';
import { Flex, Button } from 'antd';
import { Select } from 'components/Base/Select';
import DownloadButton from 'components/Shared/DownloadButton/DownloadButton';
import { useGetChartMetadataQuery } from 'redux/apiSlice/reporting.slice';
import { SCard } from './ChartCard.styles';
import ChartImage from './ChartImage/ChartImage';

type DropdownItem = {
  label: string;
  value: string;
  default?: boolean;
  filename?: string;
};

interface ChartCardProps {
  id: string;
  name: string;
}

export const ChartCard: React.FC<ChartCardProps> = ({ id, name }) => {
  const { data: metadata } = useGetChartMetadataQuery(id);

  const hasDropdownItems = useMemo(
    () => metadata?.dropdown_items || [],
    [metadata],
  );

  const dropdownItemsOptions = useMemo<DropdownItem[]>(
    () =>
      (metadata?.dropdown_items || []).map(item => ({
        label: item.title,
        value: item.filename,
        default: item.default || false,
        filename: item.filename,
      })),
    [metadata],
  );
  const defaultOption = useMemo(
    () => dropdownItemsOptions.find(option => option.default === true),
    [dropdownItemsOptions],
  );

  const [selectedOption, setSelectedOption] = useState<DropdownItem>();
  const [filename, setFilename] = useState<string>();

  const [file, setFile] = useState<string>();

  const handleChartLoaded = (svg: string) => {
    setFile(svg);
  };

  const handleSelectChange = (value: string) => {
    const newOption = dropdownItemsOptions.find(
      option => option.value === value,
    );

    if (!newOption) return;

    setSelectedOption(newOption);
    setFilename(newOption.filename);
  };

  const handleDownload = () => {
    if (file) {
      const blob = new Blob([file], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${name}.svg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  useEffect(() => {
    if (defaultOption || dropdownItemsOptions.length > 0) {
      const option = defaultOption || dropdownItemsOptions[0];
      setSelectedOption(option);
      setFilename(option.filename);
    }
  }, [defaultOption, dropdownItemsOptions]);

  return (
    <SCard data-cy="reporting-card">
      <Flex vertical className="w-100 h-100">
        <Flex justify="flex-end" align="center">
          {hasDropdownItems && (
            <Flex align="center" gap={8}>
              <Select
                value={selectedOption?.value}
                onChange={handleSelectChange}
                options={dropdownItemsOptions}
                style={{ width: 380 }}
                data-cy="reporting-card-selector"
              />
              <DownloadButton
                onClick={handleDownload}
                data-cy="reporting-card-download"
              />
            </Flex>
          )}
        </Flex>

        <Flex justify="center" align="center" className="w-100 h-100">
          {filename && (
            <ChartImage
              id={id}
              filename={filename}
              alt={name}
              onFileLoaded={handleChartLoaded}
              data-cy="reporting-card-chart-image"
            />
          )}
        </Flex>

        {!hasDropdownItems && (
          <Flex justify="center" style={{ marginTop: 16 }}>
            <Button
              type="primary"
              onClick={handleDownload}
              data-cy="reporting-card-open-report"
            >
              Open Report
            </Button>
          </Flex>
        )}
      </Flex>
    </SCard>
  );
};

export default ChartCard;
